/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import arrayMutators from 'final-form-arrays'
import { Form } from 'react-final-form'
import { findAllChapterNotes_findAllChapterNotes_items as ChapterNote } from '__generatedTypes__/findAllChapterNotes'
import { createChapterNote, createChapterNoteVariables } from '__generatedTypes__/createChapterNote'
import { updateChapterNote, updateChapterNoteVariables } from '__generatedTypes__/updateChapterNote'
import { removeChapterNote, removeChapterNoteVariables } from '__generatedTypes__/removeChapterNote'
import { CREATE_CHAPTER_NOTE, REMOVE_CHAPTER_NOTE, UPDATE_CHAPTER_NOTE } from 'queries/chapterNotes'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { InputField } from 'components/Input'
import TextareaAutosize from 'components/TextareaAutosize/TextareaAutosize'
import { MembershipLevelSelect } from 'components/MembershipLevelSelect'
import { validateRequired } from 'utils/validation'
import { toast } from 'utils/toast'
import * as classes from 'components/shared/GroupCards/commonStyles'

type ManageModalProps = {
  initialValues: Partial<ChapterNote>
  onClose: () => void
  onSubmit: () => Promise<void>
  isEditMode: boolean
}

export const ManageModal = ({ initialValues, onClose, onSubmit, isEditMode }: ManageModalProps) => {
  const [createChapterNote, { loading: createLoading }] = useMutation<createChapterNote, createChapterNoteVariables>(
    CREATE_CHAPTER_NOTE
  )
  const [updateChapterNote, { loading: updateLoading }] = useMutation<updateChapterNote, updateChapterNoteVariables>(
    UPDATE_CHAPTER_NOTE
  )
  const [removeChapterNote, { loading: removeLoading }] = useMutation<removeChapterNote, removeChapterNoteVariables>(
    REMOVE_CHAPTER_NOTE
  )

  const handleGroupRemove = async () => {
    if (!initialValues?.id) return
    try {
      await removeChapterNote({ variables: { id: initialValues.id } })
      await onSubmit()

      toast.success('Chapter Note deleted successfully')
    } catch (error) {
      toast.error('Failed to delete Chapter Note')
    }
  }

  const handleSubmit = async (data: ChapterNote) => {
    if (isEditMode) {
      try {
        await updateChapterNote({
          variables: {
            params: {
              id: data.id,
              title: data.title,
              order: data.order,
              link: data.link,
              membershipLevel: data.membershipLevel,
            },
          },
        })
        await onSubmit()

        toast.success('Chapter Note updated successfully')
      } catch (error) {
        toast.error('Failed to update Chapter Note')
      }
    } else {
      try {
        await createChapterNote({
          variables: {
            params: {
              title: data.title,
              order: initialValues.order,
              link: data.link,
              membershipLevel: data.membershipLevel,
            },
          },
        })
        await onSubmit()

        toast.success('Chapter Note created successfully')
      } catch (error) {
        toast.error('Failed to create Chapter Note')
      }
    }
  }

  return (
    <div css={classes.modalContainer}>
      <Form
        mutators={{ ...arrayMutators }}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, values }) => (
          <form css={classes.modalForm} onSubmit={handleSubmit}>
            <Typography css={classes.modalTitle} variant="title2">
              {isEditMode ? 'Edit' : 'Create'} Chapter Note
            </Typography>
            <InputField name="title" validate={validateRequired} label="Title" fullWidth />
            <TextareaAutosize
              label="URL"
              validate={validateRequired}
              css={classes.descriptionInput}
              name="link"
              value={values.link}
            />
            <MembershipLevelSelect name="membershipLevel" validate={validateRequired} fullWidth />
            <div css={classes.buttons}>
              {isEditMode && (
                <Button
                  css={classes.button}
                  variant="outlined"
                  color="red"
                  size="large"
                  onClick={handleGroupRemove}
                  disabled={createLoading || updateLoading || removeLoading}
                >
                  <TrashIcon />
                  Delete
                </Button>
              )}
              <Button css={classes.button} variant="outlined" color="blue" size="large" onClick={onClose}>
                Cancel
              </Button>
              <Button
                css={[classes.button, isEditMode && classes.fullButton]}
                variant="contained"
                type="submit"
                color="blue"
                size="large"
                disabled={createLoading || updateLoading || removeLoading}
              >
                Submit
              </Button>
            </div>
          </form>
        )}
      />
    </div>
  )
}
